import {
  ChartLayout, Inventory, LayoutList, LayoutRecord, SeriesRequest, SeriesResponse,
} from './layoutTypes';

/* Every explorer request, and every assumption about its shape, lives here. */

type Call = (url: string, options?: Record<string, unknown>) => Promise<any>;
export interface ApiLike {
  get: Call;
  post: Call;
  patch: Call;
  put: Call;
  delete: Call;
}

/* ── Inventory: one per log, small, kept for the session ── */

const inventories = new Map<string, Promise<Inventory>>();

export function fetchInventory(api: ApiLike, logId: string): Promise<Inventory> {
  const hit = inventories.get(logId);
  if (hit) return hit;
  const p = api.get(`/analysis/log-fields/${encodeURIComponent(logId)}`, { silent: true })
    .then((data: Inventory) => ({ ...data, topics: Array.isArray(data?.topics) ? data.topics : [] }));
  inventories.set(logId, p);
  // A failure must not stick: the next ask tries again.
  p.catch(() => { if (inventories.get(logId) === p) inventories.delete(logId); });
  return p;
}

/* ── Series: an LRU of 64 answers ── */

const LRU_SIZE = 64;
const seriesCache = new Map<string, SeriesResponse>();

const round = (v: number | undefined) => (v == null ? '' : v.toFixed(4));

export function seriesCacheKey(logId: string, req: SeriesRequest): string {
  return [
    logId, req.topic, req.instance, [...req.fields].sort().join(','),
    round(req.t0), round(req.t1), req.points ?? '', req.mode ?? '',
  ].join('|');
}

export async function fetchSeries(
  api: ApiLike, logId: string, req: SeriesRequest, signal?: AbortSignal,
): Promise<SeriesResponse> {
  const key = seriesCacheKey(logId, req);
  const hit = seriesCache.get(key);
  if (hit) {
    seriesCache.delete(key);
    seriesCache.set(key, hit);
    return hit;
  }
  const q = new URLSearchParams();
  q.set('topic', req.topic);
  q.set('instance', String(req.instance));
  q.set('fields', [...req.fields].sort().join(','));
  if (req.t0 != null) q.set('t0', req.t0.toFixed(4));
  if (req.t1 != null) q.set('t1', req.t1.toFixed(4));
  if (req.points != null) q.set('points', String(req.points));
  if (req.mode) q.set('mode', req.mode);
  const data: SeriesResponse = await api.get(
    `/analysis/series/${encodeURIComponent(logId)}?${q.toString()}`,
    { signal, silent: true },
  );
  seriesCache.set(key, data);
  if (seriesCache.size > LRU_SIZE) {
    const oldest = seriesCache.keys().next().value;
    if (oldest !== undefined) seriesCache.delete(oldest);
  }
  return data;
}

export const isAbort = (err: any) => err?.name === 'AbortError';

/* ── Saved profiles ── */

export const listLayouts = (api: ApiLike): Promise<LayoutList> =>
  api.get('/analysis/layouts', { silent: true }).then((d: LayoutList) => ({
    layouts: Array.isArray(d?.layouts) ? d.layouts : [],
    default_id: d?.default_id ?? null,
  }));

export const createLayout = (
  api: ApiLike, body: { name: string; shared: boolean; layout: ChartLayout },
): Promise<LayoutRecord> => api.post('/analysis/layouts', { body, silent: true });

export const updateLayout = (
  api: ApiLike, id: string, body: { name?: string; shared?: boolean; layout?: ChartLayout },
): Promise<LayoutRecord> => api.patch(`/analysis/layouts/${encodeURIComponent(id)}`, { body, silent: true });

export const deleteLayout = (api: ApiLike, id: string): Promise<unknown> =>
  api.delete(`/analysis/layouts/${encodeURIComponent(id)}`, { silent: true });

export const setDefaultLayout = (api: ApiLike, id: string | null): Promise<{ default_id: string | null }> =>
  api.put('/analysis/layouts/default', { body: { id }, silent: true });
